import { useTenant } from '@parcel2go/npm-p2g-app';
import useSWR from 'swr';
import { MyExtendedDetails } from '~/lib/api/me/types';
import { useMeApi } from '~/lib/api/me/use-me-api';
import { SettingsCache } from '~/lib/consts/settings-cache';

/**
 * Reads `/mydetails/extended` once per page, shared by every consumer.
 *
 * `useMeApi().getMyExtendedDetails` is an unconditional fetch with no caching, and the data is
 * wanted in several places at once - the VIP banner (which mounts from both the nav and the
 * sidebar) and every live-chat button. Going through SWR on a fixed key dedupes all of them onto
 * one request.
 *
 * Gated on the `VipSupport` feature exactly as the VIP banner was, so customers whose tenant does
 * not use VIP support make no request at all. Callers pass `enabled: false` when they will not
 * show a chat, so a hook that runs unconditionally (as hooks must) still costs nothing - a hidden
 * chat button or a cancellation outcome with no chat should not be fetching VIP data.
 *
 * The key carries the tenant because the response does: the API gates the VIP fields on a per-tenant
 * setting, so the same customer can be VIP on one tenant and not another. It does not carry the
 * identity - `AuthenticationProvider` in `@parcel2go/npm-p2g-app` clears every SWR key when the
 * signed-in identity changes, so one customer's details cannot be served to another.
 */
export function useExtendedDetails(enabled = true) {
  const { getMyExtendedDetails } = useMeApi();
  const { tenant } = useTenant();

  const vipSupport = SettingsCache.settings.Features?.includes('VipSupport') ?? false;

  const { data, isLoading } = useSWR<MyExtendedDetails | null>(
    enabled && vipSupport ? `my-extended-details-${tenant}` : null,
    async () => {
      const res = await getMyExtendedDetails();

      return res.success ? res.result : null;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      // Without this a consumer mounting after the deduping interval revalidates the key, so a
      // route remount would issue another request and undo the point of sharing it.
      revalidateIfStale: false
    }
  );

  return { details: data ?? null, isLoading } as const;
}
